import React, { useEffect, useRef, useState } from 'react';

/**
 * Explicit-size stand-in for recharts' `ResponsiveContainer`.
 *
 * Do not use ResponsiveContainer in this app. It waits on a ResizeObserver,
 * and this app globally replaces ResizeObserver (shared/utils/resizeObserverFix),
 * whose replacement does not reliably deliver the initial entry. The chart then
 * resolves its scales against a zero/stale size and never recomputes them.
 *
 * The failure is deceptive rather than obvious: every point collapses onto the
 * same coordinate, so a single-bucket series still renders one centred dot that
 * looks perfectly fine. It only reveals itself once there are two points — which
 * is exactly when someone is looking at a real chart and trusting it.
 *
 * Measure synchronously, then observe for later changes.
 *
 *   <div className="h-[180px]">
 *     <MeasuredBox>
 *       {({ width, height }) => <AreaChart width={width} height={height} …/>}
 *     </MeasuredBox>
 *   </div>
 */
export default function MeasuredBox({ children }) {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!ref.current) return undefined;
    const measure = () => {
      const r = ref.current?.getBoundingClientRect();
      if (r && r.width > 0 && r.height > 0) {
        setSize((prev) => (
          Math.abs(prev.width - r.width) < 1 && Math.abs(prev.height - r.height) < 1
            ? prev
            : { width: Math.floor(r.width), height: Math.floor(r.height) }
        ));
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(ref.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <div ref={ref} className="h-full w-full overflow-hidden">
      {size.width > 0 && size.height > 0 ? children(size) : null}
    </div>
  );
}
